<template>
	<ul class="StatsToolbar" :class="classes">
		<!-- @slot for one or more StatListItem components -->
		<slot />
	</ul>
</template>

<script>
import CloStatListItem from '@/visualizations/CloStatList/CloStatListItem.vue';

export default {
	components: {
		CloStatListItem,
	},
	props: {
		/**
		 * Number of columns (2, 3, 4, or 5)
		 */
		columns: {
			type: Number,
			default: null,
			validator(value) {
				return [2, 3, 4, 5].includes(value);
			},
		},
		/**
		 * Show vertical line separators
		 */
		separators: {
			type: Boolean,
			default: false,
		},
		/**
		 * Reverse style
		 */
		reverse: {
			type: Boolean,
			default: false,
		},
		/**
		 * Layout for use in a page hero layout, often with a large image
		 */
		hero: {
			type: Boolean,
			default: false,
		},
		/**
		 * Layout to spread evenly across container
		 */
		spread: {
			type: Boolean,
			default: false,
		},
		/**
		 * Layout to fit inside container
		 */
		fit: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		classes() {
			return {
				'StatsToolbar--twoAcross': this.columns === 2,
				'StatsToolbar--threeAcross': this.columns === 3,
				'StatsToolbar--fourAcross': this.columns === 4,
				'StatsToolbar--fiveAcross': this.columns === 5,
				'StatsToolbar--separators': this.separators,
				'StatsToolbar--reverse': this.reverse,
				'StatsToolbar--hero': this.hero,
				'StatsToolbar--evenSpread': this.spread,
				'StatsToolbar--fitStats': this.fit,
			};
		},
	},
};
</script>

<style lang="scss">
.StatsToolbar {
	display: flex;
	flex-wrap: wrap;
	margin: 0; /* remove default <ul> margin */
	overflow-x: auto;
	overflow-y: hidden; /* Fixes windows scroll issue */
	width: 100%;
	max-width: 100%;
	/* Override style for browsers that support css Grid */
	@supports (display: grid) {
		display: grid;
		grid-gap: $space-sm;
		grid-template-columns: repeat(auto-fit, minmax(6rem, 1fr));
		margin: 0;
		@include breakpoint(medium) {
			grid-gap: $space-md;
			grid-template-columns: repeat(auto-fit, minmax(8rem, 10rem));
		}
		@include breakpoint(xlarge) {
			grid-template-columns: repeat(auto-fit, minmax(10rem, 12rem));
		}
	}
}

/* Wrapper for each StatsIcon component */
.StatsToolbar-stat {
	flex: 1 1 auto; /* Distribute evenly on small screens */
	list-style: none;
	@include inset-xs; /* Padding to space stats when using flex */
	display: flex;
	@include breakpoint(medium) {
		flex: 0 1 auto; /* Left align (no grow) on medium up screens */
		padding-right: $space-md;
		[dir='rtl'] & {
			padding-right: $space-xs;
			padding-left: $space-md;
		}
	}
	@include breakpoint(xlarge) {
		padding-right: $space-lg;
		[dir='rtl'] & {
			padding-right: $space-xs;
			padding-left: $space-lg;
		}
	}
	.StatsIcon {
		flex: 1 1 auto;
	}
	/* Override style for browsers that support css Grid */
	@supports (display: grid) {
		padding: 0;
		margin: 0;
		.StatsIcon {
			flex: 1 1 100%; /* Force StatsIcon to fill space for maximum clickability of links */
		}
		@include breakpoint(medium) {
			margin: 0;
			padding: 0;
		}
		@include breakpoint(xlarge) {
			margin: 0;
			padding: 0;
		}
	}
}

/* ----------------
	Variants
---------------- */

/* Separators variant - adds separators to the left of each stat, excluding the first */
.StatsToolbar--separators {
	.StatsToolbar-stat {
		border-color: $color-neutral-1;
		border-style: solid;
		border-width: 0;
		&:not(:first-of-type) {
			@include breakpoint(medium) {
				border-left-width: 1px;
				padding-left: $space-sm;
				[dir='rtl'] & {
					border-left-width: 0;
					border-right-width: 1px;
					padding-right: $space-sm;
					padding-left: $space-xs;
				}
			}
			@include breakpoint(xlarge) {
				padding-left: $space-md;
				[dir='rtl'] & {
					padding-right: $space-md;
					padding-left: $space-xs;
				}
			}
		}
	}
	&.StatsToolbar--reverse {
		.StatsToolbar-stat {
			border-color: $color-neutral-4;
		}
	}
}

/* Two across variant - two columns on mobile */
.StatsToolbar--twoAcross {
	.StatsToolbar-stat {
		flex: 0 1 auto;
		min-width: 50%; // for IE11
	}
	@supports (grid-area: x) {
		grid-template-columns: repeat(2, 1fr);
	}
}

/* Three across variant - three columns on mobile */
.StatsToolbar--threeAcross {
	.StatsToolbar-stat {
		flex: 0 1 auto;
		min-width: 33.333%; // for IE11
	}
	@supports (grid-area: x) {
		grid-template-columns: repeat(3, 1fr);
	}
}

/* Four across variant - four columns on mobile */
.StatsToolbar--fourAcross {
	.StatsToolbar-stat {
		flex: 0 1 auto;
		min-width: 25%; // for IE11
	}
	@supports (grid-area: x) {
		grid-template-columns: repeat(4, 1fr);
	}
}

/* Five across variant - five columns on mobile */
.StatsToolbar--fiveAcross {
	.StatsToolbar-stat {
		flex: 0 1 auto;
		min-width: 20%; // for IE11
	}
	@supports (grid-area: x) {
		grid-template-columns: repeat(5, 1fr);
	}
}

/* Hero variant - for stats that stay in a width restricted box at larger views */
.StatsToolbar--hero {
	.StatsToolbar-stat {
		flex: 0 1 auto;
		@include breakpoint(medium) {
			flex: 1 1 auto;
			min-width: 50%; // for IE11
			margin: 0;
			padding: 0 $space-sm $space-sm 0;
			[dir='rtl'] & {
				padding: 0 0 $space-sm $space-sm;
			}
		}
		@include breakpoint(xlarge) {
			padding: 0 $space-md $space-md 0;
			[dir='rtl'] & {
				padding: 0 0 $space-md $space-md;
			}
		}
		@include breakpoint(xxlarge) {
			padding: 0 $space-md $space-md 0;
			[dir='rtl'] & {
				padding: 0 0 $space-md $space-md;
			}
		}
	}
	/* Grid override */
	@supports (grid-area: x) {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(8rem, 1fr));
		justify-items: flex-start;
		@include breakpoint(medium) {
			grid-gap: $space-md;
			grid-template-columns: repeat(2, 1fr);
		}
		@include breakpoint(xlarge) {
			grid-template-columns: repeat(2, 1fr);
			grid-gap: $space-md;
		}
		/* Unset flex padding rules */
		.StatsToolbar-stat {
			@include breakpoint(medium) {
				padding: 0;
			}
			@include breakpoint(xlarge) {
				padding: 0;
			}
			@include breakpoint(xxlarge) {
				padding: 0;
			}
		}
	}
}

.StatsToolbar--evenSpread {
	display: flex;
	justify-content: space-between;
	.StatsToolbar-stat {
		flex: 1 0 auto;
		min-width: 50%;
		padding: 0 $space-sm $space-sm 0;
		[dir='rtl'] & {
			padding: 0 0 $space-sm $space-sm;
		}
		@include breakpoint(medium) {
			flex: 1 0 auto;
			min-width: 25%;
		}
	}
	@include breakpoint(large) {
		.StatsToolbar-stat {
			flex: 1 1 auto;
			min-width: 0;
		}
		@supports (grid-area: x) {
			display: grid;
			.StatsToolbar-stat {
				padding: 0;
				min-width: 0 !important;
			}
		}
	}
}

// Fit stats removes min and max width constraints from stats containers
.StatsToolbar--fitStats {
	.StatsToolbar-stat {
		flex: 1 1 auto;
		min-width: 10rem;
	}
	@supports (grid-area: x) {
		grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
	}
}
</style>